import { createContext, useContext, useMemo } from "react";
import { useAuth } from "./store/auth";

// i18n ligera (Fase 4.5). El idioma sale de la preferencia del usuario
// (`user.language`, "es" | "en"). `t(key, fallback)` devuelve la traducción del
// idioma activo; si falta, usa el fallback (o el propio texto en español).
//
// Se traduce de forma incremental: hoy el "shell" (navegación, menú, acciones
// comunes). Las páginas que aún no usan `t()` siguen en español hasta migrarse.

export const DICT = {
  // Navegación principal
  "nav.mytasks": { es: "Mis Tareas", en: "My Tasks" },
  "nav.board": { es: "Tablero", en: "Board" },
  "nav.projects": { es: "Proyectos", en: "Projects" },
  "nav.timeline": { es: "Timeline", en: "Timeline" },
  "nav.docs": { es: "Documentación", en: "Documentation" },
  "nav.reports": { es: "Reportes", en: "Reports" },
  "nav.metrics": { es: "Métricas", en: "Metrics" },
  "nav.tools": { es: "Herramientas", en: "Tools" },
  "nav.activity": { es: "Actividad", en: "Activity" },
  "nav.messaging": { es: "Mensajería", en: "Messaging" },

  // Reportes (hijos)
  "rep.exec": { es: "Dashboard ejecutivo", en: "Executive dashboard" },
  "rep.taskmgmt": { es: "Gestión de tareas", en: "Task management" },
  "rep.projstatus": { es: "Estado actual de proyectos", en: "Current project status" },
  "rep.projreport": { es: "Resumen Proyectos", en: "Projects summary" },
  "rep.useractivity": { es: "Actividad por usuario", en: "Activity by user" },
  "rep.taskreport": { es: "Reporte de tareas", en: "Tasks report" },

  // Barra superior / acciones
  "top.task": { es: "Tarea", en: "Task" },
  "top.createTask": { es: "Crear tarea", en: "Create task" },
  "top.messaging": { es: "Mensajería", en: "Messaging" },
  "top.notifications": { es: "Actividad y notificaciones", en: "Activity & notifications" },
  "top.settings": { es: "Ajustes", en: "Settings" },
  "top.search": { es: "Buscar en la página", en: "Search this page" },
  "top.profile": { es: "Mi perfil", en: "My profile" },
  "top.logout": { es: "Cerrar sesión", en: "Log out" },
  "top.markAllRead": { es: "Marcar todas leídas", en: "Mark all read" },
  "top.viewAllActivity": { es: "Ver toda la actividad", en: "See all activity" },
  "top.noNotifications": { es: "Sin notificaciones", en: "No notifications" },

  // Menú de ajustes (grupos)
  "set.g.users": { es: "Usuarios y perfiles", en: "Users & profiles" },
  "set.g.data": { es: "Estructura de datos", en: "Data structure" },
  "set.g.comm": { es: "Datos y comunicación", en: "Data & communication" },
  "set.g.system": { es: "Sistema", en: "System" },

  // Acciones comunes
  "act.save": { es: "Guardar", en: "Save" },
  "act.cancel": { es: "Cancelar", en: "Cancel" },
  "act.create": { es: "Crear", en: "Create" },
  "act.edit": { es: "Editar", en: "Edit" },
  "act.delete": { es: "Borrar", en: "Delete" },
  "act.send": { es: "Enviar", en: "Send" },
  "act.close": { es: "Cerrar", en: "Close" },
  "act.apply": { es: "Aplicar", en: "Apply" },
  "act.loading": { es: "Cargando…", en: "Loading…" },

  // Login
  "login.welcome": { es: "Bienvenido de nuevo", en: "Welcome back" },
  "login.subtitle": { es: "Ingresa tus credenciales para continuar", en: "Enter your credentials to continue" },
  "login.password": { es: "Contraseña", en: "Password" },
  "login.forgot": { es: "¿Olvidaste tu contraseña?", en: "Forgot your password?" },
  "login.signin": { es: "Iniciar sesión", en: "Sign in" },
  "login.signingin": { es: "Ingresando…", en: "Signing in…" },
  "login.help": { es: "¿Problemas para entrar? Contacta a tu administrador de TI.", en: "Trouble signing in? Contact your IT administrator." },
  "login.tagline": { es: "La gestión de tu equipo de TI, bajo control.", en: "Your IT team's work, under control." },

  // Mi Perfil
  "prof.title": { es: "Mi Perfil", en: "My Profile" },
  "prof.subtitle": { es: "Gestiona tu avatar, contraseña y notificaciones", en: "Manage your avatar, password and notifications" },
  "prof.avatar": { es: "Elige un avatar", en: "Choose an avatar" },
  "prof.notify": { es: "¿Qué quieres que te notifiquemos?", en: "What would you like to be notified about?" },
  "prof.navprefs": { es: "Preferencias de navegación", en: "Navigation preferences" },
  "prof.shortcuts": { es: "Atajos de teclado", en: "Keyboard shortcuts" },
  "prof.contact": { es: "Datos de contacto", en: "Contact details" },
  "prof.region": { es: "Región e idioma", en: "Region & language" },
  "prof.oldtasks": { es: "Tareas antiguas", en: "Old tasks" },
  "prof.changepw": { es: "Cambiar contraseña", en: "Change password" },
  "prof.sessions": { es: "Sesiones activas", en: "Active sessions" },
  "prof.loginhistory": { es: "Mis inicios de sesión recientes", en: "My recent sign-ins" },
  "prof.f.role": { es: "Cargo", en: "Job title" },
  "prof.f.phone": { es: "Teléfono", en: "Phone" },
  "prof.f.ext": { es: "Extensión", en: "Extension" },
  "prof.f.location": { es: "Ubicación / sede", en: "Location / office" },
  "prof.f.timezone": { es: "Zona horaria", en: "Time zone" },
  "prof.f.country": { es: "País", en: "Country" },
  "prof.f.language": { es: "Idioma", en: "Language" },
  "prof.f.homepage": { es: "Página de inicio (al entrar)", en: "Home page (on sign-in)" },
  "prof.f.defaultview": { es: "Vista de tareas por defecto", en: "Default task view" },
  "prof.f.density": { es: "Densidad", en: "Density" },
  "prof.f.sound": { es: "Sonido de notificaciones", en: "Notification sound" },
  "prof.f.ficha": { es: "Ficha del proyecto", en: "Project overview" },
  "prof.saving": { es: "Guardando…", en: "Saving…" },
  "prof.logoutAll": { es: "Cerrar todas mis sesiones", en: "Log out of all sessions" },
};

const I18nCtx = createContext("es");

export function I18nProvider({ children }) {
  const { user } = useAuth();
  const lang = user?.language === "en" ? "en" : "es";
  return <I18nCtx.Provider value={lang}>{children}</I18nCtx.Provider>;
}

export function useLang() { return useContext(I18nCtx); }

export function useT() {
  const lang = useContext(I18nCtx);
  return useMemo(() => (key, fallback) => (DICT[key]?.[lang]) ?? fallback ?? key, [lang]);
}
